import { createElement as rc, Fragment, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useDarkMode from '../../hooks/useDarkMode';
import useHasFeatureFlag from '../../hooks/useHasFeatureFlag';
import { constants } from 'lib_ui-services';
import { h1, View, styled, themeValues, getAvailableThemes, hooks } from 'lib_ui-primitives';
import GlobalStyle from './createGlobalStyle';
import _isIos from './isIos';

const _p = {
    useBbState: hooks.useBbState,
    useHasFeatureFlag
};

export const _private = _p;

const { SCOPE, VISIBILITY, DURATION } = constants.retention;

const BROWSER_LEVEL_RETENTION_POLICY = {
    scope: SCOPE.GLOBAL,
    visibility: VISIBILITY.PER_BROWSER,
    duration: DURATION.ALWAYS
};
/*
(320 x 461) MC3390 total pixels (should be 'small mobile')
    web: 129920
    native: 147626.6
(360 x 590) TC5x, allowing 50 px for "Other" screen elements (should be larger than 'small mobile')
    194400 (web or native?)
*/
/* To calculate the pixels on a phone, given its screen size and PPI:
DPR = PPI /160;     -- 160 is the "Android Baseline" for pixel density
logical Width = Width / DPR;
logical Height = Height / DPR;
E.g, for the Pixel 9 Pro XL (1344 x 2992, 486 PPI):
DPR = 486/160 = 3.04
Logical Width = 1344 / 3.04 ≈ 443
Logical Height = 2992 / 3.04 ≈ 982
*/ 
const MAX_SMALL_MOBILE = 160000; // somewhere safely in between MC33 and TC5X
const MAX_MOBILE_PIXELS = 500000; // (443 x 982) Pixel 9 Pro XL
const MAX_TABLET_PIXELS = 1000000; // (834 x 1194) iPad 11"

const LoadingThemeStyle = styled(View)`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;
const Centered = styled(h1)`
    text-align: center;
`;

/**
 * Theme provider component for the application.
 *
 * Wraps children with styled-components ThemeProvider and applies global styles.
 * Handles dark/light mode logic, including support for overriding via props.
 *
 * @param {Object} props - Component props.
 * @param {'dark'|'light'|null|undefined} [props.overrideDarkMode] -
 *   If set to 'dark' or 'light', overrides the automatic dark mode logic and forces the theme to the specified mode.
 *   If null or undefined, uses the user's system or stored preference.
 *   Any other value is ignored and treated as undefined.
 * @param {'old'|'gl2025'|null|undefined} [props.overrideTheme] -
 *   If set to 'old' or 'gl2025', overrides the automatic theme logic and forces the theme to the specified value.
 * @param {React.ReactNode} [props.children] - Children to render within the theme provider.
 *
 * @returns {React.ReactElement} The themed application.
 */
export default function Theme(props) {
    const [selectedTheme, setSelectedTheme, themeReady] = _p.useBbState(
        undefined,
        'userSelectedTheme',
        BROWSER_LEVEL_RETENTION_POLICY
    );

    // Acceptable values: 'dark', 'light', null/undefined. Any other value ignored.
    const validOverrideValues = ['dark', 'light', null, undefined];
    let overrideDarkMode = props.overrideDarkMode;
    if (!validOverrideValues.includes(overrideDarkMode)) {
        overrideDarkMode = undefined;
    }
    // Acceptable values: 'old', 'gl2025', null/undefined. Any other value ignored.
    const validOverrideThemes = getAvailableThemes()
        .map(x => x.id)
        .concat([null, undefined]);
    let overrideTheme = props.overrideTheme;
    if (!validOverrideThemes.includes(overrideTheme)) {
        overrideTheme = undefined;
    }
    const defaultToGl2025 = _p.useHasFeatureFlag('gl2025');
    if (!overrideTheme && !selectedTheme && defaultToGl2025) {
        overrideTheme = 'gl2025';
    }

    const { children } = props || {};
    const { darkMode: systemDarkMode, setDarkMode, ready: darkModeReady } = useDarkMode();
    // Use override if provided and valid, otherwise use system value
    const darkMode = overrideDarkMode === 'dark' ? true : overrideDarkMode === 'light' ? false : systemDarkMode;

    const { height, width, keyboard = 0 } = useWindowDimensions();
    const isIos = useMemo(() => _isIos(), []);
    const totalPixels = height * width;
    const tablet = totalPixels <= MAX_TABLET_PIXELS && totalPixels > MAX_MOBILE_PIXELS;
    const mobile = MAX_MOBILE_PIXELS >= totalPixels;
    const smallMobile = MAX_SMALL_MOBILE >= totalPixels;
    const availableThemes = useMemo(() => (themeReady ? getAvailableThemes() : []), [themeReady]);

    // Avoid rerenders
    const theme = useMemo(() => {
        const regularTheme = themeValues({
            selectedTheme: overrideTheme ?? selectedTheme,
            keyboardHeight: keyboard,
            height,
            width,
            tablet,
            mobile,
            smallMobile,
            darkMode,
            isIos
        });
        // Need a new object if anything changes.  Otherwise, the UI won't rerender
        return { selectTheme: setSelectedTheme, selectedTheme, availableThemes, setDarkMode, ...regularTheme };
    }, [
        selectedTheme,
        overrideTheme,
        keyboard,
        height,
        width,
        tablet,
        mobile,
        smallMobile,
        darkMode,
        isIos,
        setSelectedTheme,
        availableThemes,
        setDarkMode
    ]);

    if (!darkModeReady) {
        return rc(LoadingThemeStyle, null, rc(Centered, null, 'Loading Theme Preference'));
    }

    // prettier-ignore
    return rc(Fragment, null,
        GlobalStyle ? rc(GlobalStyle, { theme }) : null,
        rc(ThemeProvider, { theme }, children)
    );
}
